/**
 * Shared schema.org helpers for venue structured data, used by the listing and
 * category pages and by the venue detail page.
 *
 * Everything here is derived from the venue record that is actually rendered on
 * the page, so `@id`, `url`, `name`, `image`, `address` and `geo` always belong
 * to the same venue. Nothing is guessed: when a field is missing in the data it
 * is left out of the schema instead of being filled with a fallback.
 */

import { getSchemaTypeForBusinessType } from "@/lib/venue-business-type";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Mekan kapalıysa panelde her iki saat de 23:23 olarak kaydediliyor.
const CLOSED_MARKER = "23:23";

/**
 * Origin used by every absolute URL we publish, matching `CanonicalTag`: https
 * + www, and tolerant of a `site.domain` that already carries the `www.` prefix
 * so we never emit `https://www.www.example.com`.
 */
export function getSiteOrigin(site: any) {
  const domain = site?.domain?.trim()?.toLowerCase();

  if (!domain) return undefined;

  return `https://${domain.startsWith("www.") ? domain : `www.${domain}`}`;
}

/**
 * Canonical venue detail URL — the same absolute form the canonical tag uses
 * (https + www, no trailing slash, no query string). Every schema block must
 * build venue URLs through this helper so that ItemList, CollectionPage and the
 * LocalBusiness on both the listing and the detail page point at the exact same
 * string, which is also what makes the detail page's `@id` the node the listing
 * pages reference.
 */
export function getVenueDetailUrl(site: any, venue: any) {
  const origin = getSiteOrigin(site);
  const venueDetailRoute = site?.setting?.routes?.venueDetail || "";
  const slug = venue?.descriptions?.[0]?.slug;
  const districtSlug = venue?.district?.slug;

  if (!origin || !slug) return undefined;

  const venuePath = districtSlug ? `${slug}-${districtSlug}` : slug;
  // A site without a venueDetail route would otherwise leave an empty segment
  // and produce `https://www.example.com//slug`.
  const path = [venueDetailRoute, venuePath].filter(Boolean).join("/");

  return `${origin}/${path}`;
}

/**
 * Titles and descriptions come out of the CMS as HTML. schema.org values are
 * plain text, so tags are dropped, entities decoded and whitespace collapsed —
 * otherwise `name` keeps the trailing spaces the panel saved and `description`
 * would publish `<p>` markup.
 */
export function toPlainText(value: unknown, maxLength?: number) {
  if (typeof value !== "string") return undefined;

  const text = value
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(p|div|li|h[1-6])>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&apos;/gi, "'")
    // Turkish characters reach us as numeric entities (`&#305;` for ı) often
    // enough that a generic decoder is worth more than a lookup table.
    .replace(/&#(\d+);/g, (_, code) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code) =>
      String.fromCodePoint(parseInt(code, 16)),
    )
    .replace(/\s+/g, " ")
    .trim();

  if (!text) return undefined;

  if (maxLength && text.length > maxLength) {
    return `${text.slice(0, maxLength).trimEnd()}…`;
  }

  return text;
}

/**
 * The panel stores hours as `HH:MM:SS`, but other sources have shown up as
 * `T08:00` or full ISO timestamps. Google expects a bare `HH:MM`, so pull the
 * first clock value out of whatever we were given and drop the rest.
 */
export function normalizeTime(value: unknown) {
  if (typeof value !== "string") return undefined;

  const match = value.match(/(\d{1,2}):(\d{2})/);

  if (!match) return undefined;

  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

/**
 * A day the venue is closed is published as `00:00`–`00:00`, which is how
 * schema.org and Google model "closed all day". Dropping the day instead — as
 * this did — leaves it undefined rather than closed, so a venue that shows
 * "Pazar: KAPALI" on the page said nothing about Sunday in its markup.
 *
 * A day with no stored hours at all is still left out: unknown is not closed,
 * and the page does not claim otherwise either.
 */
const CLOSED_ALL_DAY = "00:00";

export function getVenueOpeningHours(venue: any) {
  const openingHours = DAYS.map((day, index) => {
    const opens = normalizeTime(venue?.hours?.[`d_${index + 1}_1`]);
    const closes = normalizeTime(venue?.hours?.[`d_${index + 1}_2`]);

    if (!opens || !closes) return null;

    // The panel writes the marker into both ends; a record with it on one end
    // only is still not a business hour, so it counts as closed rather than
    // publishing 23:23 as a time the venue opens.
    const isClosed = opens === CLOSED_MARKER || closes === CLOSED_MARKER;

    return {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: day,
      opens: isClosed ? CLOSED_ALL_DAY : opens,
      closes: isClosed ? CLOSED_ALL_DAY : closes,
    };
  }).filter(Boolean);

  return openingHours.length ? openingHours : undefined;
}

/**
 * The panel stores the whole address in one field, and venues type the district
 * and the city onto the end of it — "…Özgürler Sokak 42/A, Keçiören - Ankara".
 * `streetAddress` is only the street part; the district and the city are
 * published as `addressLocality` and `addressRegion`, so leaving them on the end
 * states each of them twice.
 *
 * Only a trailing occurrence is removed, and only on a word boundary, so an
 * address on "Ankara Caddesi" in Ankara keeps its street name.
 */
function stripTrailingPlaceName(address: string, place: unknown) {
  if (typeof place !== "string") return address;

  const name = place.trim().toLocaleLowerCase("tr");

  if (!name || !address.toLocaleLowerCase("tr").endsWith(name)) return address;

  const head = address.slice(0, address.length - name.length);

  // "…42/A, Keçiören" ends with the district; "Yeni Keçiören" does not.
  if (head && !/[\s,;|/\-–—]$/.test(head)) return address;

  return head.replace(/[\s,;|\-–—]+$/, "");
}

export function getVenueStreetAddress(venue: any) {
  const address = toPlainText(venue?.descriptions?.[0]?.address);

  if (!address) return undefined;

  const places = [
    venue?.district?.title,
    venue?.district?.city?.title,
    venue?.city?.title,
  ];

  // Repeated until nothing more comes off, because the suffix is a pair:
  // dropping "Ankara" is what exposes the "Keçiören" in front of it.
  let streetAddress = address;
  let previous = "";

  while (streetAddress && streetAddress !== previous) {
    previous = streetAddress;

    for (const place of places) {
      streetAddress = stripTrailingPlaceName(streetAddress, place);
    }
  }

  return streetAddress || undefined;
}

/**
 * `district` holds the locality/semt the venue sits in and `district.city` the
 * province, so the locality is the district and the region is the city — the
 * other way around reads as "Batıkent province, Ankara town". Postal codes are
 * not in the data, so they are simply omitted.
 */
export function getVenueAddress(venue: any) {
  const streetAddress = getVenueStreetAddress(venue);
  const addressLocality = toPlainText(venue?.district?.title);
  const addressRegion = toPlainText(
    venue?.district?.city?.title || venue?.city?.title,
  );

  return {
    "@type": "PostalAddress",
    ...(streetAddress ? { streetAddress } : {}),
    ...(addressLocality ? { addressLocality } : {}),
    ...(addressRegion ? { addressRegion } : {}),
    addressCountry: "TR",
  };
}

/**
 * Google asks for `telephone` with the country and area code, and the panel
 * stores whatever the venue typed — `0(312)2151105`, `0312 215 11 05`,
 * `+905388117164`. Turkish numbers are normalised to E.164; anything that does
 * not look like one is published as typed rather than mangled into a wrong
 * number.
 */
export function normalizePhone(value: unknown) {
  if (typeof value !== "string" || !value.trim()) return undefined;

  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/g, "");

  if (digits.length === 10) return `+90${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) {
    return `+90${digits.slice(1)}`;
  }
  if (digits.length === 12 && digits.startsWith("90")) return `+${digits}`;

  return trimmed;
}

/**
 * `map_latitude`/`map_longitude` arrive as strings and are `"0"` for venues that
 * were never placed on the map, which would pin the business off the coast of
 * Ghana. Coordinates are published as numbers, and only when they are real.
 */
export function getVenueGeo(venue: any) {
  const latitude = Number(venue?.map_latitude);
  const longitude = Number(venue?.map_longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return undefined;
  }

  if (latitude === 0 && longitude === 0) return undefined;

  return {
    "@type": "GeoCoordinates",
    latitude,
    longitude,
  };
}

/**
 * Images are always read from the venue's own record (`/image/venue/<venue id>/…`),
 * preferring real photos over the logo. When the venue has neither, `image` is
 * left out rather than pointing at some other business' asset.
 *
 * `limit` keeps listing pages at one image per venue; the detail page publishes
 * the whole gallery, which is what Google asks for on the page that is about the
 * business.
 */
export function getVenueImages(site: any, venue: any, limit?: number) {
  const origin = getSiteOrigin(site);

  if (!origin || !venue?.id) return undefined;

  const photos = (Array.isArray(venue?.photos) ? venue.photos : [])
    .filter((item: any) => item?.url)
    .map((item: any) => `${origin}/image/venue/${venue.id}/photo/${item.url}`);

  const images = photos.length
    ? photos
    : venue?.logo
      ? [`${origin}/image/venue/${venue.id}/${venue.logo}`]
      : [];

  if (!images.length) return undefined;

  return limit ? images.slice(0, limit) : images;
}

export function getVenueImage(site: any, venue: any) {
  return getVenueImages(site, venue, 1);
}

/**
 * The logo is a separate schema.org property from `image`; publishing it there
 * as well means the gallery no longer has to give up its first slot to a logo.
 */
export function getVenueLogo(site: any, venue: any) {
  const origin = getSiteOrigin(site);

  if (!origin || !venue?.id || !venue?.logo) return undefined;

  return `${origin}/image/venue/${venue.id}/${venue.logo}`;
}

const BEST_RATING = 5;
const WORST_RATING = 1;

/**
 * `datePublished` has to be an ISO 8601 date. The API returns a timestamp, so
 * only the date part is published — the exact minute a review was written is
 * not information Google asks for, and a malformed value invalidates the whole
 * review.
 */
export function normalizeDate(value: unknown) {
  if (typeof value !== "string" || !value.trim()) return undefined;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return undefined;

  return date.toISOString().slice(0, 10);
}

/**
 * A comment only becomes a `Review` when the visitor actually left a star
 * rating. Comments without one are questions ("haftalık üyelik var mı?") — the
 * old markup gave every one of them a 4, which published ratings nobody gave.
 *
 * Replies are skipped as well: an answer under a review is not itself a review
 * of the business, so it must neither appear as one nor be counted in
 * `aggregateRating`.
 */
function getRatedComments(comments: any) {
  if (!Array.isArray(comments)) return [];

  return comments.filter((comment: any) => {
    const rating = Number(comment?.rating);

    return (
      Number.isFinite(rating) && rating >= WORST_RATING && rating <= BEST_RATING
    );
  });
}

export function getVenueReviews(comments: any, publisherName?: string) {
  const reviews = getRatedComments(comments).map((comment: any) => {
    const datePublished = normalizeDate(comment?.date);
    const reviewBody = toPlainText(comment?.text);

    return {
      "@type": "Review",
      author: {
        "@type": "Person",
        name: toPlainText(comment?.userName) || "Anonim",
      },
      reviewRating: {
        "@type": "Rating",
        ratingValue: Number(comment.rating),
        bestRating: BEST_RATING,
        worstRating: WORST_RATING,
      },
      ...(reviewBody ? { reviewBody } : {}),
      ...(datePublished ? { datePublished } : {}),
      ...(publisherName
        ? { publisher: { "@type": "Organization", name: publisherName } }
        : {}),
    };
  });

  return reviews.length ? reviews : undefined;
}

/**
 * `reviewCount` counts the ratings that come with written feedback and
 * `ratingCount` all of them, instead of the previous total that also included
 * every reply and every unrated comment.
 */
export function getVenueAggregateRating(comments: any) {
  const rated = getRatedComments(comments);

  if (!rated.length) return undefined;

  const total = rated.reduce(
    (sum: number, comment: any) => sum + Number(comment.rating),
    0,
  );
  const reviewCount = rated.filter((comment: any) =>
    toPlainText(comment?.text),
  ).length;

  return {
    "@type": "AggregateRating",
    ratingValue: Math.round((total / rated.length) * 10) / 10,
    ratingCount: rated.length,
    ...(reviewCount ? { reviewCount } : {}),
    bestRating: BEST_RATING,
    worstRating: WORST_RATING,
  };
}

/**
 * Google asks for the most specific `LocalBusiness` subtype that is true of the
 * business, so a pilates studio is a `HealthClub` and a sports school a
 * `SportsActivityLocation` instead of both settling for the bare fallback.
 *
 * The venue record carries no category, so the subtype is read off the two
 * things the page itself shows: the venue's name and the services it lists.
 * Google Places types (`sports_school`, `fitness_center`) belong to a different
 * vocabulary and are never valid as `@type`, so nothing here passes a raw
 * source value through — every branch returns one of three schema.org types.
 */
const BUSINESS_TYPES = {
  fitness: "HealthClub",
  sports: "SportsActivityLocation",
  fallback: "LocalBusiness",
} as const;

/**
 * Matching happens on a folded form of the text — Turkish letters flattened,
 * punctuation collapsed — because the panel holds "Kickboks", "Kick Boks" and
 * "kick-boks" for the same thing.
 */
function foldForMatching(value: unknown) {
  if (typeof value !== "string") return "";

  return value
    .toLocaleLowerCase("tr")
    .replace(/ç/g, "c")
    .replace(/ğ/g, "g")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ş/g, "s")
    .replace(/ü/g, "u")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * Terms match at a word start, so "spor okul" still finds "Spor Okulları" while
 * "mma" no longer finds "Emma".
 */
function matchesTerm(text: string, term: string) {
  return ` ${text}`.includes(` ${term}`);
}

/**
 * One entry per discipline rather than per service title, so a studio listing
 * "Pilates", "Mat Pilates" and "Pilates Reformer" counts as offering pilates
 * once instead of looking like three different things.
 */
const SPORTS_CONCEPTS: Record<string, string[]> = {
  archery: ["okculuk"],
  athletics: ["atletizm"],
  badminton: ["badminton"],
  basketball: ["basketbol"],
  climbing: ["tirmanis"],
  equestrian: ["binicilik"],
  fencing: ["eskrim"],
  football: ["futbol", "hali saha", "kaleci"],
  // Stems, because the panel and the venues themselves spell it "Jimnastik",
  // "Cimnastik" and "Cimnastica" — one of the sibling sites is even called
  // ankaracimnastikokullari.com.
  gymnastics: ["jimnast", "cimnast", "gymnast"],
  handball: ["hentbol"],
  "martial-arts": [
    "aikido",
    "boks",
    "dovus",
    "fight",
    "gures",
    "jiu jitsu",
    "ju jitsu",
    "judo",
    "karate",
    "kickboks",
    "kick boks",
    "krav maga",
    "kung fu",
    "mma",
    "muay thai",
    "ninjutsu",
    "savunma sanat",
    "savunma spor",
    "taekwondo",
    "tekvando",
    "wushu",
  ],
  skating: ["paten"],
  skiing: ["kayak"],
  // "sport" as well as "sports", so that "Sport Center" is not typed
  // differently from the "Sports Center" down the road. Turkish "spor" is
  // deliberately not here on its own: it would swallow "Spor Salonu", which is
  // a gym.
  "sports-centre": ["spor merkez", "spor kulub", "sport"],
  "sports-school": ["spor okul", "spor akadem", "spor lisesi", "besyo"],
  swimming: ["yuzme"],
  tennis: ["tenis"],
  volleyball: ["voleybol"],
};

const FITNESS_CONCEPTS: Record<string, string[]> = {
  aerobics: ["aerobik"],
  bodybuilding: ["vucut gelistirme", "body building", "bodybuilding"],
  cardio: ["cardio", "kardiyo", "spinning"],
  crossfit: ["crossfit", "cross fit"],
  fitness: ["fitness", "fit"],
  functional: ["fonksiyonel antren", "functional training"],
  gym: ["gym", "spor salonu"],
  "personal-training": ["personal train", "kisisel antren", "ozel antren"],
  pilates: ["pilates", "pilago", "reformer"],
  wellness: ["wellness"],
  yoga: ["yoga"],
};

/**
 * Businesses that are none of the above and say so in their name. Every site
 * shares this resolver, and dance schools, beauty salons, physiotherapists and
 * dietitians all list a pilates or a yoga class among forty other services —
 * without this they would come out as `HealthClub`, and a physiotherapy clinic
 * is not a health club. Naming one of these is not enough to pick a subtype for
 * them, so they take the `LocalBusiness` fallback, which is what Google's own
 * mapping does for a dance school.
 */
const OTHER_BUSINESS_TERMS = [
  "anaokul",
  "beauty",
  "berber",
  "dance",
  "dans",
  "diyet",
  "dyt",
  "epilasyon",
  "estetik",
  "fizik tedavi",
  "fizyo",
  "fzt",
  "guzellik",
  "kafe",
  "konservatuar",
  "kres",
  "kuafor",
  "makyaj",
  "muzik",
  "psikolog",
  "restoran",
  "sanat",
  "surucu kursu",
  "tiyatro",
];

function countConcepts(texts: string[], concepts: Record<string, string[]>) {
  return Object.values(concepts).filter((terms) =>
    texts.some((text) => terms.some((term) => matchesTerm(text, term))),
  ).length;
}

// Enough distinct disciplines that the venue is plainly one kind of place, not
// one stray service on a list of sixty. Service lists are noisy — a beauty
// centre offers "Slim Up (Pasif Cimnastik)" and a dance school offers pilates —
// so a single match decides nothing.
const CONCEPT_THRESHOLD = 2;

/**
 * The operator's answer wins whenever there is one.
 *
 * `venue.business_type` is picked from a fixed Turkish list in the panel and
 * stored on the record, so a veterinary clinic comes out as `VeterinaryCare`
 * and a meyhane as `BarOrPub` — neither of which the heuristic below can reach,
 * since it only ever returns three types. Guessing is what happens when the
 * field is empty, not what happens instead of asking.
 *
 * `name` and `services` are what the page shows about the business, so they are
 * what the guess is derived from. The name is read first and on its own: it is
 * the venue's own statement of what it is, and it is the one field that is never
 * padded. Anything the data does not clearly support falls back to
 * `LocalBusiness` rather than inventing a subtype.
 */
export function getVenueBusinessType(venue: any, name?: string) {
  const selected = getSchemaTypeForBusinessType(venue?.business_type);

  if (selected) return selected;

  const venueName = foldForMatching(
    name ?? venue?.descriptions?.[0]?.title ?? "",
  );
  const services = (Array.isArray(venue?.services) ? venue.services : [])
    .map((item: any) =>
      foldForMatching(item?.service?.descriptions?.[0]?.title),
    )
    .filter(Boolean);

  // A sports school that also runs fitness classes is still a sports school, so
  // sports is read before fitness — CM Sports Studio, with basketball, BESYO
  // prep and sports-academy prep alongside its pilates, is that case.
  if (countConcepts([venueName], SPORTS_CONCEPTS)) {
    return BUSINESS_TYPES.sports;
  }

  // Read before the other-business terms, because a name that carries both is
  // naming its trade with the specific half: "Fizyoterapist Burcu Özdemir
  // Pilates" is a pilates studio run by a physiotherapist, not a clinic that
  // happens to list pilates.
  if (countConcepts([venueName], FITNESS_CONCEPTS)) {
    return BUSINESS_TYPES.fitness;
  }

  if (OTHER_BUSINESS_TERMS.some((term) => matchesTerm(venueName, term))) {
    return BUSINESS_TYPES.fallback;
  }

  const sports = countConcepts(services, SPORTS_CONCEPTS);
  const fitness = countConcepts(services, FITNESS_CONCEPTS);

  // One family has to be both above the threshold and ahead of the other. A tie
  // means the service list describes two kinds of business equally well, which
  // is not a basis for picking either — Wox Academy lists as much physiotherapy
  // as it does martial arts, and stays a `LocalBusiness`.
  if (sports >= CONCEPT_THRESHOLD && sports > fitness) {
    return BUSINESS_TYPES.sports;
  }

  if (fitness >= CONCEPT_THRESHOLD && fitness > sports) {
    return BUSINESS_TYPES.fitness;
  }

  return BUSINESS_TYPES.fallback;
}

const DESCRIPTION_MAX_LENGTH = 500;

// Some venues have sixty photos. Google picks from the leading ones, and the
// whole gallery is in the page markup anyway, so the schema stays readable in
// Search Console instead of listing every file.
const IMAGE_LIMIT = 10;

/**
 * Absolute URL for the venue's own site, when it stored one. It is published as
 * `sameAs` rather than `url`, because `url` is the detail page — the venue's
 * homepage is a different resource that happens to describe the same business.
 */
function getExternalUrl(value: unknown) {
  if (typeof value !== "string") return undefined;

  const trimmed = value.trim();

  if (!trimmed || trimmed.includes(" ")) return undefined;

  const withProtocol = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  try {
    const url = new URL(withProtocol);

    if (!url.hostname.includes(".")) return undefined;

    return url.toString();
  } catch {
    return undefined;
  }
}

/**
 * Services the venue really offers, expressed the way schema.org models a
 * service list. Prices are not in the data, so the offers carry none.
 */
function getOfferCatalog(venue: any, name: string) {
  const services = (Array.isArray(venue?.services) ? venue.services : [])
    .map((item: any) => toPlainText(item?.service?.descriptions?.[0]?.title))
    .filter(Boolean);

  const unique = Array.from(new Set(services));

  if (!unique.length) return undefined;

  return {
    "@type": "OfferCatalog",
    name,
    itemListElement: unique.map((service) => ({
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: service,
      },
    })),
  };
}

interface VenueDetailSchemaParams {
  venue: any;
  site: any;
  comments: any;
  district?: string;
  slug?: string;
}

/**
 * The venue detail page's whole JSON-LD node, built from the venue record the
 * page renders. It lives here rather than in the component so the same object
 * can be asserted on directly in a test, without a browser or a running site.
 */
export function getVenueDetailSchema({
  venue,
  site,
  comments,
  district,
  slug,
}: VenueDetailSchemaParams) {
  if (!venue) return null;

  const origin = getSiteOrigin(site);
  const venueDetailRoute = site?.setting?.routes?.venueDetail;

  // The listing pages reference venues by their detail URL, so the same helper
  // has to produce this page's `@id`; the route params are only a fallback for
  // records without a stored slug.
  const url =
    getVenueDetailUrl(site, venue) ||
    (origin && venueDetailRoute && slug
      ? `${origin}/${venueDetailRoute}/${district ? `${slug}-${district}` : slug}`
      : undefined);

  if (!url) return null;

  const name = toPlainText(venue?.descriptions?.[0]?.title);

  if (!name) return null;

  const geo = getVenueGeo(venue);
  const siteName = toPlainText(site?.descriptions?.[0]?.title);

  return {
    "@context": "https://schema.org",
    // The most specific subtype the venue's own data supports, which is what
    // Google asks for; `LocalBusiness` when it supports none.
    "@type": getVenueBusinessType(venue, name),
    "@id": url,
    url,
    name,
    description: toPlainText(
      venue?.descriptions?.[0]?.description,
      DESCRIPTION_MAX_LENGTH,
    ),
    image: getVenueImages(site, venue, IMAGE_LIMIT),
    logo: getVenueLogo(site, venue),
    address: getVenueAddress(venue),
    geo,
    hasMap: geo
      ? `https://www.google.com/maps/search/?api=1&query=${geo.latitude},${geo.longitude}`
      : undefined,
    telephone: normalizePhone(venue?.phones?.[0]?.phone),
    email: toPlainText(venue?.descriptions?.[0]?.email),
    sameAs: getExternalUrl(venue?.website),
    openingHoursSpecification: getVenueOpeningHours(venue),
    hasOfferCatalog: getOfferCatalog(venue, `${name} hizmetleri`),
    aggregateRating: getVenueAggregateRating(comments),
    review: getVenueReviews(comments, siteName),
  };
}

/**
 * One `LocalBusiness` node for a venue as it appears on a listing page: the
 * same `@id` and subtype the detail page publishes, with a single image and no
 * ratings, since a category page is not about any one business.
 */
export function getVenueLocalBusiness(site: any, venue: any) {
  const url = getVenueDetailUrl(site, venue);
  const name = toPlainText(venue?.descriptions?.[0]?.title);

  if (!url || !name) return null;

  return {
    "@type": getVenueBusinessType(venue, name),
    "@id": url,
    url,
    name,
    image: getVenueImage(site, venue),
    address: getVenueAddress(venue),
    geo: getVenueGeo(venue),
    telephone: normalizePhone(venue?.phones?.[0]?.phone),
    openingHoursSpecification: getVenueOpeningHours(venue),
  };
}

/**
 * The venue cards a page renders, as one `ItemList` of `LocalBusiness` nodes —
 * the same shape the listing pages publish, so a venue described on the
 * homepage, on a blog post and on a category page is described identically
 * everywhere and always under the `@id` of its detail page.
 *
 * A venue can appear twice in one list (a featured venue that is also nearby,
 * a blog that links the same venue from two services); publishing two
 * `ListItem`s with one `@id` reads as two businesses, so repeats are dropped
 * and the first position is kept.
 *
 * Returns `null` when nothing publishable is left, so callers can render
 * nothing rather than an empty list.
 */
export function buildVenueItemList(site: any, venues: any, id: string) {
  const list = Array.isArray(venues) ? venues : [];
  const seenIds = new Set<string>();

  const businesses = list
    .map((venue: any) => getVenueLocalBusiness(site, venue))
    .filter((business: any) => {
      if (!business || seenIds.has(business["@id"])) return false;

      seenIds.add(business["@id"]);

      return true;
    });

  if (!businesses.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": id,
    numberOfItems: businesses.length,
    itemListElement: businesses.map((business: any, index: number) => ({
      "@type": "ListItem",
      position: index + 1,
      item: business,
    })),
  };
}

export { DAYS, CLOSED_MARKER, BEST_RATING, WORST_RATING };
